import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api } from '@/api/client';
import type { ReportDetail } from '@/api/types';

const POLL_INTERVAL_MS = 2500;

export interface UseReportPollingResult {
  data?: ReportDetail;
  loading: boolean;
  /** An i18n key describing the last fetch failure, or `null` once a fetch has succeeded. */
  error: string | null;
  /** Re-fetches immediately and resumes/stops the interval based on the fresh status. */
  refresh: () => Promise<void>;
}

/**
 * Polls `GET /reports/:id` every 2.5s while `report.status === 'generating'`, stopping the
 * interval the moment it sees any other status (mirrors `useSessionPolling`, one report at a
 * time). `refresh()` re-fetches on demand — e.g. after `retry`/`reopen` — and re-arms the
 * interval if the report is generating again.
 */
export const useReportPolling = (reportId: string): UseReportPollingResult => {
  const [data, setData] = useState<ReportDetail | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (timer.current) {
      clearInterval(timer.current);
      timer.current = null;
    }
  }, []);

  const poll = useCallback(async (): Promise<ReportDetail | undefined> => {
    try {
      const detail = await api.reports.get(reportId);
      setData(detail);
      setError(null);
      return detail;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'common.error');
      return undefined;
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  const startPolling = useCallback(() => {
    if (timer.current) return;
    timer.current = setInterval(() => {
      void poll().then((detail) => {
        if (!detail || detail.report.status !== 'generating') stopPolling();
      });
    }, POLL_INTERVAL_MS);
  }, [poll, stopPolling]);

  useEffect(() => {
    let cancelled = false;
    setData(undefined);
    setLoading(true);
    setError(null);
    stopPolling();

    void (async () => {
      const detail = await poll();
      if (!cancelled && detail?.report.status === 'generating') startPolling();
    })();

    return () => {
      cancelled = true;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  const refresh = useCallback(async () => {
    const detail = await poll();
    if (detail?.report.status === 'generating') startPolling();
    else stopPolling();
  }, [poll, startPolling, stopPolling]);

  return { data, loading, error, refresh };
};
